import { useEffect, useMemo, useState } from 'react';
import { useForm, FormProvider, type Resolver } from "react-hook-form";
import { zodResolver } from '@hookform/resolvers/zod';

import Text from "@/components/forms/text";
import SmallSpinner from "@/components/shared/spinners/small-spinner";
import Autocomplete, { type AutocompleteItem } from "@/components/forms/autocomplete";
import { getCitiesFromFrance, getCountries } from "@/lib/helpers";
import AutoCompleteBirth from "@/components/forms/auto-complete-birth";
import Select from "@/components/forms/select";
import { listFormeJuridique } from "@/lib/options/select-options";
import { useNavigate } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { User, Building2, Save } from 'lucide-react';
import { useContracts } from '@/hooks/api/use-contracts';
import { useUpdateBirthInfos } from '@/hooks/api/use-profile';
import {
    createChangeInformationsNaissanceSchema,
    type ChangeInformationsNaissanceValues,
} from '@/schemas/change-informations-naissance';

interface Country {
    id: string;
    name: string;
}

const ChangeInformationsNaissance = () => {
    const contractsQuery = useContracts();

    if (contractsQuery.isPending) return <SmallSpinner />;

    return <ChangeInformationsNaissanceForm />;
};

const ChangeInformationsNaissanceForm = () => {
    const contractsQuery = useContracts();
    const souscripteur = contractsQuery.data?.data[0]?.souscripteur;
    const societe = souscripteur?.titre === 'SOC';
    const initialValues: ChangeInformationsNaissanceValues = {
        pays: souscripteur?.code_pays_naissance,
        ville: souscripteur?.ville_naissance,
        nom: souscripteur?.nom_naissance,
        statut_juridique: souscripteur?.statut_juridique,
        siren_siret: souscripteur?.num_siren_siret,
    };
    const postUpdateBirthInfosTiers = useUpdateBirthInfos();
    const loading = postUpdateBirthInfosTiers.isPending;
    const [countries, setCountries] = useState<AutocompleteItem[]>();
    const navigate = useNavigate();

    const resolver = useMemo(
        () => zodResolver(createChangeInformationsNaissanceSchema(societe)) as unknown as Resolver<ChangeInformationsNaissanceValues>,
        [societe],
    );

    const methods = useForm<ChangeInformationsNaissanceValues>({
        defaultValues: initialValues,
        resolver,
        mode: 'onSubmit',
    });

    const { handleSubmit, formState: { errors }, watch, setValue, register } = methods;
    const values = watch();

    useEffect(() => {
        void (async function fetchCountries() {
            const countries = await getCountries<Country[]>();
            const listcountries = countries.map(c => ({
                value: c.id,
                label: c.name
            }));
            setCountries(listcountries);
        })();
    }, []);

    return (
        <div className="space-y-6">
            <div className="space-y-2 sm:space-y-3 mt-4">
                <h1 className="text-xl sm:text-2xl font-bold">Je modifie mes informations personnelles</h1>
            </div>

            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(values => {
                    if (values.pays === 'FRA' && values.ville) {
                        void getCitiesFromFrance(values.ville, 'ValidateFormBirth');
                    }
                    postUpdateBirthInfosTiers.mutate({ form: values, successFunction: () => { void navigate('/mon-profil'); } });
                })}>
                    <Card>
                        <CardHeader className="pb-6 border-b">
                            <CardTitle className="text-lg flex items-center gap-2">
                                {societe
                                    ? <Building2 className="w-5 h-5 text-primary" />
                                    : <User className="w-5 h-5 text-primary" />
                                }
                                {societe ? 'Informations société' : 'Informations de naissance'}
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-6 space-y-5">
                            {!societe ? (
                                <>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                        <div>
                                            {countries && (
                                                <Autocomplete
                                                    name="pays"
                                                    label="Pays de naissance"
                                                    data={countries}
                                                    defaultValue={countries.find(c => c.value === initialValues.pays)?.label}
                                                    setValue={setValue}
                                                    error={errors.pays?.message}
                                                />
                                            )}
                                        </div>
                                        <div>
                                            {values.pays === 'FRA' ? (
                                                <AutoCompleteBirth
                                                    name="ville"
                                                    label="Ville de naissance"
                                                    country={values.pays}
                                                    defaultValue={values.ville}
                                                    setValue={setValue}
                                                />
                                            ) : (
                                                <Text
                                                    type="text"
                                                    label="Ville de naissance"
                                                    {...register('ville')}
                                                    value={values.ville}
                                                    error={errors.ville?.message}
                                                />
                                            )}
                                        </div>
                                    </div>

                                    <div className="max-w-md">
                                        <Text
                                            type={'text'}
                                            label={'Nom de naissance'}
                                            {...register('nom')}
                                            value={values.nom}
                                            error={errors.nom?.message}
                                            maxLength={20}
                                        />
                                    </div>
                                </>
                            ) : (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                    <Select
                                        name="statut_juridique"
                                        label={'Statut juridique'}
                                        value={values.statut_juridique}
                                        error={errors.statut_juridique?.message}
                                        required
                                    >
                                        <option value="">Sélectionnez le statut juridique de l'entreprise</option>
                                        {listFormeJuridique.map((item, index) => (
                                            <option key={index} value={item.id}>{item.name}</option>
                                        ))}
                                    </Select>
                                    <Text
                                        type={'tel'}
                                        label={'N° SIREN/SIRET'}
                                        {...register('siren_siret')}
                                        value={values.siren_siret}
                                        error={errors.siren_siret?.message}
                                        required
                                    />
                                </div>
                            )}

                            <div className="pt-2">
                                {loading ? (
                                    <SmallSpinner />
                                ) : (
                                    <Button type="submit" className="w-full text-white cursor-pointer whitespace-normal py-6">
                                        <Save className="w-4 h-4 shrink-0" />
                                        Je valide mes informations personnelles
                                    </Button>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </form>
            </FormProvider>
        </div>
    );
};

export default ChangeInformationsNaissance;
